import PropTypes from "prop-types";
import { cloneElement, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

// material-ui
import { useColorScheme, useTheme } from "@mui/material/styles";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import MuiAppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";

// project imports
import { ThemeMode } from "config";
import Logo from "ui-component/Logo";

// assets
import {
  IconDashboard,
  IconHome2,
  IconMail,
  IconQuestionMark,
} from "@tabler/icons-react";
import MenuIcon from "@mui/icons-material/Menu";

function ElevationScroll({ children, window }) {
  const theme = useTheme();
  const { colorScheme } = useColorScheme();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window,
  });

  return cloneElement(children, {
    elevation: trigger ? 1 : 0,
    style: {
      backgroundColor:
        colorScheme === ThemeMode.DARK && trigger
          ? theme.vars.palette.dark[800]
          : theme.vars.palette.background.default,
      color: theme.vars.palette.text.dark,
    },
  });
}

// ==============================|| MINIMAL LAYOUT APP BAR ||============================== //

export default function AppBar({ ...others }) {
  const [drawerToggle, setDrawerToggle] = useState(false);

  const drawerToggler = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerToggle(open);
  };

  return (
    <ElevationScroll {...others}>
      <MuiAppBar>
        <Container>
          <Toolbar sx={{ py: 2.5, px: `0 !important` }}>
            <Typography
              component={RouterLink}
              to="/rally-list"
              sx={{ flexGrow: 1, textAlign: "left" }}
            >
              <Logo />
            </Typography>
            <Stack
              direction="row"
              sx={{
                gap: { xs: 1.5, md: 2.5 },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <Button color="inherit" component={RouterLink} to="/rally-list">
                Rallies
              </Button>
              <Button color="inherit" component={RouterLink} to="/login">
                Dashboard
              </Button>
              <Button color="inherit" component={RouterLink} to="/pages/faqs">
                FAQs
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/pages/contact-us"
              >
                Contact
              </Button>
            </Stack>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <IconButton
                color="inherit"
                onClick={drawerToggler(true)}
                size="large"
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="top"
                open={drawerToggle}
                onClose={drawerToggler(false)}
              >
                {drawerToggle && (
                  <Box
                    sx={{ width: "auto" }}
                    role="presentation"
                    onClick={drawerToggler(false)}
                    onKeyDown={drawerToggler(false)}
                  >
                    <List>
                      <ListItemButton component={RouterLink} to="/rally-list">
                        <ListItemIcon>
                          <IconHome2 />
                        </ListItemIcon>
                        <ListItemText primary="Rallies" />
                      </ListItemButton>
                      <ListItemButton component={RouterLink} to="/pages/faqs">
                        <ListItemIcon>
                          <IconQuestionMark />
                        </ListItemIcon>
                        <ListItemText primary="FAQs" />
                      </ListItemButton>
                      <ListItemButton
                        component={RouterLink}
                        to="/pages/contact-us"
                      >
                        <ListItemIcon>
                          <IconMail />
                        </ListItemIcon>
                        <ListItemText primary="Contact" />
                      </ListItemButton>
                      <ListItemButton component={RouterLink} to="/login">
                        <ListItemIcon>
                          <IconDashboard />
                        </ListItemIcon>
                        <ListItemText primary="Dashboard" />
                      </ListItemButton>
                    </List>
                  </Box>
                )}
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </MuiAppBar>
    </ElevationScroll>
  );
}

ElevationScroll.propTypes = { children: PropTypes.node, window: PropTypes.any };
